/*
 * TPGoogleMap — Uber-style dark Google Maps for the TP Live Board.
 * - Dark theme, interactive pan/zoom
 * - Custom markers for pickup (green), dropoff (red), drivers (avatars)
 * - When `trip` is selected, auto-fits bounds to include pickup + dropoff + drivers
 * - Draws polyline from pickup → dropoff (via Directions API when available,
 *   falls back to a straight line so the map never blocks on API quota)
 * - onDriverClick(driver) and onTripFocus(trip) for interactivity
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Marker, DirectionsRenderer, Polyline, InfoWindow } from "@react-google-maps/api";
import { Loader2, MapPinned } from "lucide-react";

const MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ["places"];

// Uber-flavoured dark style
const DARK_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#0F1720" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0F1720" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8892A0" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#1B2530" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#D1D9E1" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#232E3A" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#161F29" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2B3946" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1F2932" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#B6C1CC" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0A1219" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4B6178" }] },
];

const OPTIONS = {
  styles: DARK_STYLES,
  disableDefaultUI: true,
  zoomControl: true,
  gestureHandling: "greedy",
  clickableIcons: false,
  backgroundColor: "#0F1720",
};

const DETROIT = { lat: 42.3314, lng: -83.0458 };

function svgMarker(color, glyph) {
  const svg = `data:image/svg+xml;utf-8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='42' height='52' viewBox='0 0 42 52'>
      <defs><filter id='s' x='-20%' y='-20%' width='140%' height='140%'>
        <feDropShadow dx='0' dy='2' stdDeviation='2' flood-color='#000' flood-opacity='0.45'/>
      </filter></defs>
      <path filter='url(%23s)' d='M21 2C11 2 3 10 3 20c0 13 18 30 18 30s18-17 18-30C39 10 31 2 21 2z' fill='${color}' stroke='#fff' stroke-width='2'/>
      <circle cx='21' cy='20' r='9' fill='#fff'/>
      <text x='21' y='24' text-anchor='middle' font-family='Arial' font-size='12' font-weight='700' fill='${color}'>${glyph}</text>
    </svg>`
  )}`;
  return svg;
}

function driverAvatarSvg(color, initial) {
  return `data:image/svg+xml;utf-8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'>
      <defs><filter id='sh' x='-20%' y='-20%' width='140%' height='140%'>
        <feDropShadow dx='0' dy='2' stdDeviation='2' flood-color='#000' flood-opacity='0.5'/>
      </filter></defs>
      <circle filter='url(%23sh)' cx='20' cy='20' r='16' fill='${color}' stroke='#fff' stroke-width='3'/>
      <text x='20' y='25' text-anchor='middle' font-family='Arial' font-size='15' font-weight='700' fill='#fff'>${initial}</text>
    </svg>`
  )}`;
}

export default function TPGoogleMap({
  pickup,
  dropoff,
  drivers = [],
  onDriverClick,
  height = 460,
  emptyLabel = "Pick a trip below to focus the map",
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: MAP_KEY || "",
    libraries: LIBRARIES,
  });
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const dirServiceRef = useRef(null);
  const lastRouteKeyRef = useRef(null);

  const onLoad = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);

  // Fit bounds ONLY when the pickup/dropoff coordinates change (i.e., a different trip is selected).
  // We intentionally do NOT depend on `drivers` here — driver-position polling updates the markers
  // in-place without ever snapping the user's viewport back. This is what fixes the "map keeps
  // reloading the same trip" bug: previously every 6-8s poll re-created the drivers array
  // reference and re-fit the map, blocking the dispatcher from panning or picking another trip.
  const focusKey = useMemo(
    () => `${pickup?.lat ?? ""}:${pickup?.lng ?? ""}=>${dropoff?.lat ?? ""}:${dropoff?.lng ?? ""}`,
    [pickup?.lat, pickup?.lng, dropoff?.lat, dropoff?.lng],
  );
  useEffect(() => {
    if (!map || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let has = false;
    if (pickup?.lat != null && pickup?.lng != null) {
      bounds.extend({ lat: pickup.lat, lng: pickup.lng }); has = true;
    }
    if (dropoff?.lat != null && dropoff?.lng != null) {
      bounds.extend({ lat: dropoff.lat, lng: dropoff.lng }); has = true;
    }
    if (has) {
      map.fitBounds(bounds, 80);
      const listener = window.google.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > 15) map.setZoom(15);
      });
      return () => window.google.maps.event.removeListener(listener);
    }
  }, [map, focusKey]);

  // Fetch directions between pickup and dropoff
  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!pickup?.lat || !pickup?.lng || !dropoff?.lat || !dropoff?.lng) { setDirections(null); return; }
    const key = `${pickup.lat},${pickup.lng}=>${dropoff.lat},${dropoff.lng}`;
    if (lastRouteKeyRef.current === key) return;
    lastRouteKeyRef.current = key;
    if (!dirServiceRef.current) dirServiceRef.current = new window.google.maps.DirectionsService();
    dirServiceRef.current.route({
      origin: { lat: pickup.lat, lng: pickup.lng },
      destination: { lat: dropoff.lat, lng: dropoff.lng },
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === "OK") setDirections(result);
      else setDirections(null); // will fall back to straight polyline
    });
  }, [isLoaded, pickup, dropoff]);

  const straightLine = useMemo(() => {
    if (!pickup?.lat || !dropoff?.lat) return null;
    return [
      { lat: pickup.lat, lng: pickup.lng },
      { lat: dropoff.lat, lng: dropoff.lng },
    ];
  }, [pickup, dropoff]);

  const containerStyle = { width: "100%", height };

  if (loadError) {
    return (
      <div style={{ height, background: "#0F1720", borderRadius: 16 }} className="grid place-items-center text-slate-400 text-sm">
        <div className="text-center">
          <MapPinned className="w-6 h-6 mx-auto mb-2 opacity-60"/>
          Map failed to load. Check REACT_APP_GOOGLE_MAPS_API_KEY.
        </div>
      </div>
    );
  }
  if (!isLoaded) {
    return (
      <div style={{ height, background: "#0F1720", borderRadius: 16 }} className="grid place-items-center text-slate-400 text-sm">
        <Loader2 className="w-5 h-5 animate-spin"/>
      </div>
    );
  }

  const hasFocus = !!(pickup?.lat && dropoff?.lat);

  return (
    <div className="rounded-2xl overflow-hidden shadow-inner border border-slate-800" data-testid="tp-google-map" style={{ position: "relative", background: "#0F1720" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={pickup?.lat ? { lat: pickup.lat, lng: pickup.lng } : DETROIT}
        zoom={hasFocus ? 12 : 10}
        options={OPTIONS}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {pickup?.lat != null && (
          <Marker
            position={{ lat: pickup.lat, lng: pickup.lng }}
            icon={{ url: svgMarker("#16A34A", "P"), scaledSize: new window.google.maps.Size(42, 52), anchor: new window.google.maps.Point(21, 50) }}
            title="Pickup"
            zIndex={5}
          />
        )}
        {dropoff?.lat != null && (
          <Marker
            position={{ lat: dropoff.lat, lng: dropoff.lng }}
            icon={{ url: svgMarker("#DC2626", "D"), scaledSize: new window.google.maps.Size(42, 52), anchor: new window.google.maps.Point(21, 50) }}
            title="Drop-off"
            zIndex={5}
          />
        )}

        {directions ? (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { strokeColor: "#22D3EE", strokeOpacity: 0.9, strokeWeight: 5 },
              preserveViewport: true,
            }}
          />
        ) : straightLine ? (
          <Polyline
            path={straightLine}
            options={{ strokeColor: "#22D3EE", strokeOpacity: 0.6, strokeWeight: 3, geodesic: true }}
          />
        ) : null}

        {drivers.map((d) => (
          d.last_seen_lat != null && d.last_seen_lng != null && (
            <Marker
              key={d.driver_id}
              position={{ lat: d.last_seen_lat, lng: d.last_seen_lng }}
              icon={{
                url: driverAvatarSvg(d.shift_status === "on_shift" ? "#0EA5E9" : "#64748B", (d.name || "?")[0].toUpperCase()),
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              }}
              onClick={() => setSelectedDriver(d)}
              title={`${d.name}${d.distance_mi != null ? ` · ${d.distance_mi} mi` : ""}`}
              zIndex={4}
            />
          )
        ))}

        {selectedDriver && selectedDriver.last_seen_lat && (
          <InfoWindow
            position={{ lat: selectedDriver.last_seen_lat, lng: selectedDriver.last_seen_lng }}
            onCloseClick={() => setSelectedDriver(null)}
          >
            <div style={{ minWidth: 180 }}>
              <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 4 }}>{selectedDriver.name}</div>
              <div style={{ fontSize: 12, color: "#475569" }}>
                {selectedDriver.vehicle_type || "sedan"} · {selectedDriver.shift_status}
                {selectedDriver.distance_mi != null && <> · <b>{selectedDriver.distance_mi} mi</b> away</>}
              </div>
              {onDriverClick && (
                <button
                  onClick={() => { onDriverClick(selectedDriver); setSelectedDriver(null); }}
                  style={{ marginTop: 8, background: "#1F5F45", color: "#fff", padding: "4px 10px", borderRadius: 6, fontSize: 12, border: 0, cursor: "pointer" }}
                >
                  Assign this driver
                </button>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {!hasFocus && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="bg-black/60 backdrop-blur-sm text-white text-xs px-4 py-2 rounded-full flex items-center gap-2">
            <MapPinned size={12}/> {emptyLabel}
          </div>
        </div>
      )}
    </div>
  );
}
